<template>
  <div>
    <v-row class="custom-row-billing mb-1">
      <v-col>
        <div class="d-flex justify-end">
          <div class="mt-sm-2">
            <v-icon color="#000" class="mr-1">mdi-calendar-month</v-icon>
            <span>Periodo</span>
          </div>
          <div class="mx-5 custom-select-billing-year wx-150">
            <custom-select
              v-model="period"
              :options="periods"
              :disabled="backorders.loading"
              placeholder="Año"
            />
          </div>
        </div>
      </v-col>
    </v-row>
    <v-simple-table class="my-table w-full">
      <template #default>
        <thead class="table-heading">
          <tr class="text-center">
            <th rowspan="2" class="text-center text-dark border-lr">
              Participación <br />de familias
            </th>
            <th colspan="5" class="text-center boder-x-none text-dark">
              Backorder
            </th>
            <th rowspan="2" class="text-center text-dark border-lr">
              Backorder <br />
              cancelado <br />del mes
            </th>

            <th colspan="3" class="text-center text-dark border-lr">Crédito</th>

            <th rowspan="2" class="text-center text-dark border-lr">Pagaré</th>
          </tr>
          <tr>
            <th class="text-center text-dark">Total</th>
            <th class="text-center text-dark boder-x-none">Disponible</th>
            <th class="text-center text-dark boder-x-none">Parcial</th>
            <th class="text-center text-dark boder-x-none">Sin inventario</th>
            <th class="text-center text-dark boder-x-none">Programado</th>

            <th class="text-center text-dark boder-x-none">Límite</th>
            <th class="text-center text-dark boder-x-none">Disponible</th>
            <th class="text-center text-dark boder-x-none">Vencido</th>
          </tr>
        </thead>
        <tbody>
          <tr class="font-weight-bold">
            <template v-if="!backorders.loading">
              <td>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <a @click="getSalesByArea()">
                      <v-icon v-bind="attrs" color="#B71C1C" v-on="on"
                        >mdi-chart-pie</v-icon
                      >
                    </a>
                  </template>
                  <span>Mostrar participación/ Divisiones</span>
                </v-tooltip>
              </td>
              <td>
                {{ formatNumber(backorders.data?.backorders.total, '$', 0) }}
              </td>
              <td class="in-stock-text">
                {{ formatNumber(backorders.data?.backorders.inStock, '$', 0) }}
              </td>
              <td class="partial-stock-text">
                {{
                  formatNumber(backorders.data?.backorders.partialStock, '$', 0)
                }}
              </td>
              <td class="no-stock-text">
                {{ formatNumber(backorders.data?.backorders.noStock, '$', 0) }}
              </td>
              <td class="programmed-stock-text">
                {{
                  formatNumber(
                    backorders.data?.backorders.programmedStock,
                    '$',
                    0
                  )
                }}
              </td>
              <td>
                {{ formatNumber(backorders.data?.backorders.canceled, '$', 0) }}
              </td>
            </template>
            <template v-else>
              <td colspan="7">
                <v-progress-circular indeterminate color="primary" />
              </td>
            </template>
            <template v-if="backorders.error">
              <td colspan="7">
                <alert-prominent
                  :message="backorders.error"
                  :dismissible="true"
                />
              </td>
            </template>

            <template v-if="creditState.loading">
              <td colspan="3">
                <v-progress-circular indeterminate color="primary" />
              </td>
            </template>
            <template v-else>
              <td>{{ formatNumber(creditState.data?.creditLimit, '$', 0) }}</td>
              <td>{{ formatNumber(creditState.data?.available, '$', 0) }}</td>
              <td>{{ formatNumber(creditState.data?.due, '$', 0) }}</td>
            </template>
            <template v-if="creditState.error">
              <td colspan="3">
                <alert-prominent
                  :message="creditState.error"
                  :dismissible="true"
                />
              </td>
            </template>
            <td class="font-weight-bold pa-0 text-white">
              <alert-prominent
                v-if="promissoryNotes.errorMessage"
                :message="promissoryNotes.errorMessage"
                :dismissible="true"
              />
              <v-progress-circular
                v-if="promissoryNotes.loading"
                indeterminate
                color="primary"
              />
              <template v-if="promissoryNotes.data">
                <div class="d-flex d-inline justify-center">
                  <v-avatar
                    v-if="promissoryNotes.data.valid"
                    min-height="15"
                    max-height="30"
                    min-width="15"
                    max-width="30"
                    class="in-stock-bg ml-1"
                  >
                    <span class="text-caption">{{
                      formatFloat(promissoryNotes.data.valid, 0)
                    }}</span>
                  </v-avatar>
                  <v-avatar
                    v-if="promissoryNotes.data.inVailidityPeriod"
                    min-height="15"
                    max-height="30"
                    min-width="15"
                    max-width="30"
                    class="partial-stock-bg ml-1"
                  >
                    <span>{{
                      formatFloat(promissoryNotes.data.inVailidityPeriod, 0)
                    }}</span>
                  </v-avatar>
                  <v-avatar
                    v-if="promissoryNotes.data.expired"
                    min-height="15"
                    max-height="30"
                    min-width="15"
                    max-width="30"
                    class="no-stock-bg ml-1"
                  >
                    <span>{{
                      formatFloat(promissoryNotes.data.expired, 0)
                    }}</span>
                  </v-avatar>
                </div>
              </template>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <graph
      v-model="salesByArea.modal"
      :char-title="salesByArea.charTitle"
      :loading="salesByArea.loading"
      :data="salesByArea.data"
      :errors="salesByArea.error"
      @onPeriodChange="updateSalesByArea"
    />
  </div>
</template>

<script>
  import axios from '@/plugins/axios';
  import mixinService from '@/mixins/service';
  import activeUsernameMixin from '@/mixins/activeUsernameMixin';
  import dates from '@/mixins/dates';
  import numbersFormats from '@/mixins/numbersFormats';
  import {
    getSalesByArea,
    getBackorders,
    getDailyCredit,
  } from '@/repositories/Daily';

  import CustomSelect from '@/components/CustomSelect.vue';
  import Graph from '@/views/daily/modals/Graph.vue';
  import AlertProminent from '@/components/AlertProminent.vue';
  import { MetaRoles } from '@/core/constants/roleConstants';

  export default {
    name: 'Backorders',
    components: { CustomSelect, AlertProminent, Graph },
    mixins: [mixinService, activeUsernameMixin, dates, numbersFormats],
    props: {
      userId: {
        default: '',
        type: String,
      },
      chnl: {
        required: true,
        type: String,
      },
    },
    data() {
      return {
        username: '',
        backorders: {
          loading: true,
          data: [],
          error: '',
        },
        salesByArea: {
          loading: true,
          data: [],
          modal: false,
          error: null,
          year: null,
          quarter: null,
          charTitle: '',
        },
        creditState: {
          loading: true,
          data: [],
          error: '',
        },
        promissoryNotes: {
          loading: true,
          data: [],
          errorMessage: '',
        },
        period: null,
        periods: [],
      };
    },
    computed: {
      commonParams() {
        return {
          user: this.username,
          channel: this.chnl,
          period: this.period,
        };
      },
    },
    watch: {
      period() {
        this.getCreditState();
        this.getPromissoryNotes();
      },
    },
    created() {
      var dates = this.calcularUltimosMeses(24);
      this.periods = dates.map((item) => ({
        label: item.date,
        value: this.periodFormat(item),
        year: item.year,
        month: item.month,
      }));
      this.period = this.periods[0].value;
      const quarters = this.getQuartersList(1);
      this.salesByArea.quarter = quarters[0].quarter;
      this.salesByArea.year = quarters[0].year;
    },
    mounted() {
      this.username = this.userId ? this.userId : this.activeUsername?.username;
      this.getBackorders();
    },
    methods: {
      async getSalesByArea(period = null) {
        this.salesByArea.charTitle = `Participación por division`;
        this.salesByArea.loading = true;
        this.salesByArea.modal = true;
        period = period ? period : this.salesByArea;
        try {
          this.salesByArea.data = await getSalesByArea(
            this.username,
            this.chnl,
            period.quarter,
            period.year
          );
        } catch (error) {
          this.salesByArea.modal = false;
          this.$toast.error(
            'Hubo problemas al cargar la participación por división, por favor intente más tarde.'
          );
        }
        this.salesByArea.loading = false;
      },

      async getBackorders() {
        this.backorders.loading = true;
        this.backorders.data = [];
        this.backorders.error = '';
        try {
          this.backorders.data = await getBackorders(
            this.username,
            this.chnl,
            this.period
          );
        } catch (error) {
          this.backorders.error =
            'Ha ocurrido un error al intentar obtener los backorders';
        }
        this.backorders.loading = false;
      },

      async getCreditState() {
        this.creditState.loading = true;
        this.creditState.error = '';
        try {
          this.creditState.data = await getDailyCredit(
            this.username,
            this.chnl
          );
        } catch (error) {
          this.creditState.error =
            'Ha ocurrido un error al intentar obtener créditos';
        }
        this.creditState.loading = false;
      },
      resolvePromissoryNoteConfig() {
        const metaRole = this.activeUsername?.metaRole;
        switch (metaRole) {
          case MetaRoles.Director:
          case MetaRoles.Subdirector:
            return {
              url: '/territorial-director/home/promissory-stats',
              params: {
                channel: this.chnl,
                period: this.period,
                isRM: false,
              },
            };
          case MetaRoles.RegionalManager:
            return {
              url: '/territorial-director/home/promissory-stats',
              params: {
                user: this.username,
                channel: this.chnl,
                period: this.period,
                isRM: true,
              },
            };
          case MetaRoles.Manager:
            return {
              url: '/manager/home/promissory-stats',
              params: {
                managerId: this.username,
                channel: this.chnl,
                period: this.period,
              },
            };
          default:
            return { url: '', params: {} };
        }
      },
      async getPromissoryNotes() {
        this.promissoryNotes.loading = true;
        this.promissoryNotes.data = [];
        this.promissoryNotes.errorMessage = '';
        try {
          const config = this.resolvePromissoryNoteConfig();
          const { data } = await axios.post(config.url, config.params);
          this.promissoryNotes.data = data;
        } catch (error) {
          this.promissoryNotes.errorMessage =
            'Ha ocurrido un error al intentar obtener los pagarés';
        }
        this.promissoryNotes.loading = false;
      },
      updateSalesByArea(period) {
        this.salesByArea.year = period.year;
        this.salesByArea.quarter = period.quarter;
        this.getSalesByArea(period, period.division);
      },
    },
  };
</script>
